import { Injectable, NgZone } from '@angular/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Platform } from '@ionic/angular';
import { fromByteArray, toByteArray } from 'base64-js';
import type * as fs_typeonly from 'fs';
import { escapeRegExp } from 'lodash';
import type * as path_typeonly from 'path';
import type * as lockfile_typeonly from 'proper-lockfile';
import { OperationOptions } from 'retry';
import { TextDecoder, TextEncoder } from 'text-encoding-shim';
import type * as util_typeonly from 'util';
let fs: typeof fs_typeonly;
let path: typeof path_typeonly;
let util: typeof util_typeonly;
let lockfile: typeof lockfile_typeonly;
import { ValuePromiser } from './core';
import { electronBridge } from './electron-bridge';
import { FileIo, FileIoWriter, Metadata } from './file-io';
import './instance';

declare function __non_webpack_require__(p: string): any;

// Use require directly in Electron, fallback to __non_webpack_require__ for webpack compatibility
const nodeRequire =
    typeof __non_webpack_require__ !== 'undefined' ? __non_webpack_require__ : typeof require !== 'undefined' ? require : undefined;

if (nodeRequire) {
    try {
        fs = nodeRequire('fs');
    } catch {}
    try {
        path = nodeRequire('path');
    } catch {}
    try {
        util = nodeRequire('util');
    } catch {}
    try {
        lockfile = nodeRequire('proper-lockfile');
    } catch {}
}

class FileIoWriterRealCapacitor {
    uri: string | undefined;

    constructor(private zone: NgZone) {}

    open = async (uri: string): Promise<void> => {
        this.uri = uri;
    };

    write = async (data: string | ArrayBufferLike): Promise<void> => {
        if (this.uri === undefined) {
            throw new Error('FileIoWriter is not open');
        }
        if (typeof data !== 'string') {
            const b64 = fromByteArray(new Uint8Array(data));
            await Filesystem.appendFile({
                path: this.uri,
                data: b64,
            }).onZone(this.zone);
        } else {
            await Filesystem.appendFile({
                path: this.uri,
                data,
                encoding: Encoding.UTF8,
            }).onZone(this.zone);
        }
    };

    writeBinary = async (data: ArrayBufferLike): Promise<void> => {
        await this.write(data);
    };

    close = async (): Promise<void> => {
        this.uri = undefined;
    };
}

class FileIoWriterRealElectron {
    private fd: number | undefined;

    open = async (pathOrUrl: string): Promise<void> => {
        if (!util || !fs) {
            throw 'Node.js modules not available';
        }
        this.fd = await util.promisify(fs.open)(pathOrUrl, 'w');
    };

    write = async (data: string | Buffer): Promise<void> => {
        if (!util || !fs) {
            throw 'Node.js modules not available';
        }
        if (this.fd === undefined) {
            throw 'Write failed, file is not open';
        }
        const bin = typeof data === 'string' ? new TextEncoder('utf-8').encode(data) : data;
        await util.promisify(fs.write)(this.fd, bin);
    };

    writeBinary = async (data: ArrayBufferLike): Promise<void> => {
        await this.write(Buffer.from(data as ArrayBuffer)); // Buffer.from accepts ArrayBuffer | SharedArrayBuffer
    };

    close = async (): Promise<void> => {
        if (!util || !fs) {
            throw 'Node.js modules not available';
        }
        if (this.fd === undefined) {
            throw 'Close failed, file is not open';
        }
        await util.promisify(fs.close)(this.fd);
        this.fd = undefined;
    };
}

@Injectable({ providedIn: 'root' })
export class FileIoReal extends FileIo {
    appCacheDir: string;
    logDir: string;
    userDir: string;

    private readySource = new ValuePromiser<string>();

    constructor(
        private platform: Platform,
        private zone: NgZone,
    ) {
        super();
        this.platform.ready().then(async (source) => {
            if (source === 'cordova') {
                this.appCacheDir = (await Filesystem.getUri({ directory: Directory.Cache, path: '' })).uri;
                this.userDir = (await Filesystem.getUri({ directory: Directory.External, path: '' })).uri;
                this.logDir = this.joinPath(this.userDir, 'logs');
            } else if (electronBridge && path) {
                this.appCacheDir = electronBridge.statics.app.path.userData;
                this.logDir = path.join(electronBridge.statics.app.path.userData, 'logs');
                this.userDir = electronBridge.statics.app.path.documents;
            }
            this.readySource.notify(source);
        });
    }

    waitReady = async () => {
        await this.readySource.get();
    };

    read = async (pathOrUrl: string): Promise<string> => {
        const bin = await this.readBinary(pathOrUrl);
        return new TextDecoder().decode(new Uint8Array(bin));
    };

    readBinary = async (pathOrUrl: string): Promise<ArrayBufferLike> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            const b64 = (await Filesystem.readFile({ path: pathOrUrl }).onZone(this.zone)).data as string;
            const uint8Array = toByteArray(b64);
            return uint8Array.buffer.slice(uint8Array.byteOffset, uint8Array.byteOffset + uint8Array.byteLength) as ArrayBuffer;
        } else if (electronBridge && util && fs) {
            const data = await util.promisify(fs.readFile)(pathOrUrl);
            if (!(data.buffer instanceof ArrayBuffer)) {
                throw { message: 'Unexpected value type from readFile', data };
            }
            return new Uint8Array(data).buffer;
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    write = async (pathOrUrl: string, data: string): Promise<void> => {
        const bin = new TextEncoder().encode(data);
        return await this.writeBinary(pathOrUrl, new Uint8Array(bin).buffer);
    };

    writeBinary = async (pathOrUrl: string, data: ArrayBufferLike): Promise<void> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            const b64 = fromByteArray(new Uint8Array(data));
            await Filesystem.writeFile({ path: pathOrUrl, data: b64 }).onZone(this.zone);
        } else if (electronBridge && util && fs) {
            await util.promisify(fs.writeFile)(pathOrUrl, Buffer.from(data));
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    createWriter = async (): Promise<FileIoWriter> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            return new FileIoWriterRealCapacitor(this.zone);
        } else if (electronBridge) {
            return new FileIoWriterRealElectron();
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    delete = async (pathOrUrl: string): Promise<void> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            await Filesystem.deleteFile({ path: pathOrUrl }).onZone(this.zone);
        } else if (electronBridge && util && fs) {
            await util.promisify(fs.unlink)(pathOrUrl);
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    rename = async (from: string, to: string): Promise<void> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            await Filesystem.rename({ from, to }).onZone(this.zone);
        } else if (electronBridge && util && fs) {
            try {
                await util.promisify(fs.rename)(from, to);
            } catch (err) {
                if ((err as NodeJS.ErrnoException).code === 'EXDEV') {
                    await util.promisify(fs.copyFile)(from, to);
                    await util.promisify(fs.unlink)(from);
                } else {
                    throw err;
                }
            }
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    copy = async (from: string, to: string): Promise<void> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            await Filesystem.copy({ from, to }).onZone(this.zone);
        } else if (electronBridge && util && fs) {
            await util.promisify(fs.copyFile)(from, to);
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    readDirectory = async (pathOrUrl: string): Promise<string[]> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            const res = await Filesystem.readdir({ path: pathOrUrl }).onZone(this.zone);
            return res.files.map((file) => file.name);
        } else if (electronBridge && util && fs) {
            return await util.promisify(fs.readdir)(pathOrUrl);
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    getMetadata = async (pathOrUrl: string): Promise<Metadata> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            if (pathOrUrl) {
                const res = await Filesystem.stat({ path: pathOrUrl }).onZone(this.zone);
                const modificationTime = new Date(Number(res.mtime));
                return { modificationTime, size: res.size };
            } else {
                throw 'No pathOrUrl provided';
            }
        } else if (electronBridge && util && fs) {
            const stats = await util.promisify(fs.stat)(pathOrUrl);
            if (!(stats.mtime instanceof Date) || typeof stats.size !== 'number') {
                throw 'Unexpected return from fs.stat on ' + pathOrUrl;
            }
            return { modificationTime: stats.mtime, size: stats.size };
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    exists = async (pathOrUrl: string): Promise<boolean> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            try {
                const res = await Filesystem.stat({ path: pathOrUrl }).onZone(this.zone);
                return res.type === 'file' || res.type === 'directory';
            } catch {
                return false;
            }
        } else if (electronBridge && util && fs) {
            return await util.promisify(fs.exists)(pathOrUrl);
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    isFile = async (pathOrUrl: string): Promise<boolean> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            try {
                const res = await Filesystem.stat({ path: pathOrUrl }).onZone(this.zone);
                return res.type === 'file';
            } catch {
                return false;
            }
        } else if (electronBridge && util && fs) {
            const stat = await util.promisify(fs.stat)(pathOrUrl);
            return stat.isFile();
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    isWritable = async (pathOrUrl: string): Promise<boolean> => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            try {
                // Try to append an empty buffer to the file to determine if we have write permission
                await Filesystem.appendFile({
                    path: pathOrUrl,
                    data: '',
                }).onZone(this.zone);
                return true;
            } catch (err) {
                return false;
            }
        } else if (electronBridge && util && fs) {
            try {
                await util.promisify(fs.access)(pathOrUrl, fs.constants.W_OK);
                return true;
            } catch {
                return false;
            }
        } else {
            throw { message: 'File I/O not available', code: 'ENOENT' };
        }
    };

    basename(pathOrUrl: string, ext?: string) {
        if (electronBridge) {
            return path.basename(pathOrUrl, ext);
        } else {
            const extRegex = ext ? escapeRegExp(ext) : '';
            const regex = new RegExp('.*/([^/]*)' + extRegex);
            const match = regex.exec(pathOrUrl);
            return match ? match[1] : '';
        }
    }

    joinPath(...paths: string[]): string {
        if (electronBridge && path) {
            return path.join(...paths);
        } else {
            // Filter out undefined/null values
            const validPaths = paths.filter((p) => p != null);
            if (validPaths.length === 0) {
                return '';
            }
            // strip only trailing slashes from first entry, in case it's absolute
            const first = validPaths.slice(0, 1).map((p) => p.replace(/\/*$/, ''));
            // strip leading and trailing slashes from others
            const rest = validPaths.slice(1).map((p) => p.replace(/^\/*/, '').replace(/\/*$/, ''));

            return [...first, ...rest].join('/');
        }
    }

    get pathSep(): string {
        if (electronBridge) {
            return path.sep;
        } else {
            return '/';
        }
    }

    createDirRecursive = async (pathOrUrl: string) => {
        const readySource = await this.readySource.get();

        if (readySource === 'cordova') {
            try {
                const res = await Filesystem.stat({ path: pathOrUrl });
                if (res.type !== 'directory') {
                    throw new Error(`${pathOrUrl} exists but is not a directory`);
                }
            } catch {
                await Filesystem.mkdir({ path: pathOrUrl, recursive: true }).onZone(this.zone);
            }
        } else if (electronBridge && util && fs) {
            await util.promisify(fs.mkdir)(pathOrUrl, { recursive: true });
        }
    };

    isNotFoundError(err: any): boolean {
        if (electronBridge) {
            return err === 'ENOENT' || err?.code === 'ENOENT';
        }
        // Capacitor's iOS/Android Filesystem plugin phrases this differently across versions
        // (e.g. "File does not exist", "File does not exist.", "...at <path>"). Substring-match
        // to stay tolerant of these variations.
        const msg = typeof err?.message === 'string' ? err.message : '';
        return /does not exist|no such file/i.test(msg);
    }

    lock(file: string, options?: OperationOptions): Promise<() => Promise<void>> {
        if (electronBridge && lockfile) {
            return lockfile.lock(file, { retries: options, realpath: false });
        } else {
            throw new Error('Lock is not implemented on Capacitor');
        }
    }
}
